import React, { useContext, useState } from 'react'
import deepEqual from 'deep-equal'

import Input from '../../components/elem/form/input/Input'
import Select from '../../components/elem/form/Select'
import MultiSelect from '../../components/elem/form/MultiSelect'
import DateSelect from '../../components/elem/form/date/DateSelect'
import Checkbox from '../../components/elem/form/Checkbox'
import SubmissionInput from '../../components/features/submissions/form/Input'
import SubmissionSelect from '../../components/features/submissions/form/Select'
import DateTimeSelect from '../../components/features/submissions/form/DateTimeSelect'
import SubmissionDateSelect from '../../components/features/submissions/form/DateSelect'
import SubmissionCreatable from '../../components/features/submissions/form/Creatable'
import { useFormContext } from 'react-hook-form'
import CollapseButton from '../../components/elem/list/CollapseButton'
import { UserContext } from '../../components/wrappers/UserContext'
import { decapitalizeText } from '../stringUtils'
import { checkRole } from '../user/permissions'

// import SubmissionCheckBox from '../../components/features/submissions/form/CheckBox'
import SubmissionTextArea from '../../components/features/submissions/form/TextArea.js'
const isNullOrEmpty = (value) => {
    return value === null || value === undefined || value === ''
}

const formValidate = (
    field,
    getValues,
    setValue,
    errors,
    setError,
    clearError
) => {
    // validation on lat/lng
    if (field.ControlName === 'Radius') {
        const values = getValues()
        const latValue = values['LatitudeValue']
        const lonValue = values['LongitudeValue']
        const radiusValue = values['RadiusValue']

        const hasLatValue = !isNullOrEmpty(latValue)
        const hasLonValue = !isNullOrEmpty(lonValue)
        const hasRadiusValue = !isNullOrEmpty(radiusValue)

        if (hasLatValue || hasLonValue || hasRadiusValue) {
            if (hasLatValue && hasLonValue && !hasRadiusValue) {
                setValue('RadiusValue', field.DefaultValue)
                if (errors['RadiusValue']) {
                    clearError('RadiusValue')
                }
                return
            }

            if (!(hasLatValue && hasLonValue && hasRadiusValue)) {
                const currentError = errors['RadiusValue']
                const errorMessage =
                    'Please set latitude, longitude, and radius values.'

                if (!currentError || currentError.message !== errorMessage) {
                    setError('RadiusValue', {
                        type: 'manual',
                        message: errorMessage,
                    })
                }
                return
            }

            // Case 3: All three fields are valid - clear any existing error
            if (errors['RadiusValue']) {
                clearError('RadiusValue')
                return
            }
        } else {
            // All fields are empty - clear any existing error
            if (errors['RadiusValue']) {
                clearError('RadiusValue')
            }
        }
    }
}

const uploadValidate = () => {}

const formInputSelector = (formType) => {
    switch (formType) {
        case 'TextBox':
            return Input
        case 'Select':
            return Select
        case 'MultiSelect':
            return MultiSelect
        case 'DatePicker':
            return DateSelect
        case 'CheckBox':
            return Checkbox
        default:
            return null
    }
}

const uploadInputSelector = (formType) => {
    switch (formType) {
        case 'TextBox':
            return SubmissionInput
        case 'TextArea':
            return SubmissionTextArea
        case 'Select':
            return SubmissionSelect
        case 'MultiSelect':
            return SubmissionSelect
        case 'DatePicker':
            return DateTimeSelect
        case 'DateDayPicker':
            return SubmissionDateSelect
        case 'CheckBox':
            return Checkbox
        case 'Creatable':
            return SubmissionCreatable
        default:
            return null
    }
}

const comparisonOperatorParser = (compareString) => {
    return compareString ? compareString.split(';') : null
}

const registerParamConstructor = (field, applyRequired) => {
    const required = field.Required ? 'This field is required' : false

    // const min = field.MinValue
    //     ? {
    //           value: field.MinValue,
    //           message: `Value must be greater than ${field.MinValue}`,
    //       }
    //     : null

    // const max = field.MaxValue
    //     ? {
    //           value: field.MaxValue,
    //           message: `Value cannot be greater than ${field.MaxValue}`,
    //       }
    //     : null

    const maxLength = field.MaxLength
        ? {
              value: field.MaxLength,
              message: `Must be ${field.MaxLength} characters or less`,
          }
        : null

    const validate =
        field.DataType === 'Int' && field.ControlType === 'TextBox'
            ? (value) => {
                  return value
                      ? parseInt(value) === parseFloat(value) ||
                            'Please enter an integer value for this field'
                      : true
              }
            : null

    return {
        required: applyRequired ? required : false,
        // min,
        // max,
        maxLength,
        validate,
    }
}

const getExtraClass = (dataType) => {
    switch (dataType) {
        case 'FloatNoArrows':
            return 'input-no-arrows'
        case 'IntNoArrows':
            return 'input-no-arrows'
        default:
            return ''
    }
}

const getInputType = (dataType) => {
    switch (dataType) {
        case 'Int':
            return 'number'
        case 'Float':
            return 'number'
        default:
            return 'text'
    }
}

const formFieldParser = (
    formFields,
    register,
    unregister,
    control,
    getValues,
    setValue,
    setError,
    clearError,
    errors,
    formWidth,
    formName,
    hrColorClass,
    watch,
    validate,
    formInputSelector,
    isSectionCollapsible
) => {
    const fieldGroups = [...new Set(formFields.map((x) => x.GroupName))].filter(
        (x) => !!x
    )
    return fieldGroups.map((fieldGroup, fieldGroupIdx) => {
        const groupFields = formFields.filter((x) => x.GroupName === fieldGroup)
        const description = formFields.find(
            (x) => x.GroupName === fieldGroup
        ).GroupDescription
        return (
            <React.Fragment key={`fieldGroup-${fieldGroupIdx}`}>
                <FieldGroup
                    fieldGroup={fieldGroup}
                    description={description}
                    colorClass={hrColorClass}
                    isCollapsible={isSectionCollapsible}
                >
                    {groupFields.map((field, idx) => {
                        // filter field Display controls
                        // whether it should be in the form
                        const display = field.DisplayInFilter
                        if (!display) {
                            return null
                        }

                        const formComponent = formInputSelector(
                            field.ControlType
                        )
                        const comparisonOperators = comparisonOperatorParser(
                            field.CompareItems
                        )
                        validate(
                            field,
                            getValues,
                            setValue,
                            errors,
                            setError,
                            clearError
                        )
                        const registerParams = registerParamConstructor(
                            field,
                            false
                        )
                        const type = getInputType(field.DataType)
                        const name = field.ControlName
                        const helper = field.Description
                        const fieldName = field.Prompt
                        const values = field.Values
                        const minValue = field.MinValue
                        const maxValue = field.MaxValue
                        const maxLength = field.MaxLength
                        const dataAccessor = field.DataAccessor
                        // const error = errors ? errors[`${name}Value`] : null
                        const fieldAccessorMap = {
                            label: 'codedescription',
                            value: 'code',
                        }
                        if (!formComponent) {
                            return null
                        }
                        return React.createElement(formComponent, {
                            register,
                            unregister,
                            key: `field-${idx}`,
                            control,
                            registerParams,
                            setValue,
                            clearError,
                            helper,
                            idx,
                            fieldName,
                            values,
                            name,
                            formName,
                            dataAccessor,
                            type,
                            comparisonOperators,
                            fieldAccessorMap,
                            errors,
                            formWidth,
                            minValue,
                            maxValue,
                            maxLength,
                            watch,
                        })
                    })}
                </FieldGroup>
            </React.Fragment>
        )
    })
}

const FieldGroup = ({
    fieldGroup,
    description,
    colorClass,
    isCollapsible,
    children,
}) => {
    const [isCollapsed, setCollapsed] = useState(true)
    return (
        <>
            {fieldGroup && (
                <div className={`column is-full ${'no-vertical-padding'}`}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: 10,
                        }}
                    >
                        <div>
                            <span className="label is-medium fieldGroupText">
                                {fieldGroup}
                            </span>
                        </div>
                        <div>
                            {isCollapsible && (
                                <CollapseButton
                                    name={`sample-filters-${fieldGroup}`}
                                    isCollapsed={isCollapsed}
                                    setCollapsed={setCollapsed}
                                />
                            )}
                        </div>
                    </div>
                    <hr
                        className={`is-primary fieldGroupSeparator ${colorClass}`}
                    ></hr>
                    {description && (
                        <div className="column is-full">
                            <span
                                className="label is-small fieldGroupSeparatorText"
                                dangerouslySetInnerHTML={{
                                    __html: description,
                                }}
                            ></span>
                        </div>
                    )}
                </div>
            )}
            <div
                className={`column no-padding ${
                    isCollapsible && isCollapsed ? 'is-collapsed' : ''
                }`}
                inert={isCollapsible && isCollapsed ? 'true' : undefined}
            >
                <div className="columns is-multiline explorerForm">
                    {children}
                </div>
            </div>
        </>
    )
}
const uploadFieldParser = (
    formFields,
    formWidth,
    formName,
    hrColorClass,
    formInputSelector,
    viewOnly,
    printable,
    submissionState
) => {
    const fieldGroups = [
        ...new Set(formFields.map((x) => x.UploadSectionGroupName)),
    ]
    return fieldGroups.map((fieldGroup, fieldGroupIdx) => {
        const groupFields = formFields.filter(
            (x) => x.UploadSectionGroupName === fieldGroup
        )
        return (
            <React.Fragment key={`fieldGroup-${fieldGroupIdx}`}>
                {groupFields.map((field, idx) => {
                    // filter field Display controls
                    // whether it should be in the form
                    const display = field.DisplayInFilter
                    if (!display) {
                        return null
                    }
                    const fieldProps = {
                        field,
                        formInputSelector,
                        getInputType,
                        validate: uploadValidate,
                        formWidth,
                        printable,
                        formName,
                        hrColorClass,
                        idx,
                        displayLabel: true,
                        key: `field-component-${idx}`,
                        submissionState,
                        carefulRowData:
                            submissionState &&
                            submissionState[field.DataAccessor],
                    }
                    return <FieldComponent {...fieldProps} />
                })}
            </React.Fragment>
        )
    })
}

const determineIfDependsOnExistingFormFieldTrue = (
    dependsOnExistingFormField,
    submissionState
) => {
    const valueAccessor = dependsOnExistingFormField.split(',')[0]
    const isExistingRecordAccessor = dependsOnExistingFormField.split(',')[1]
    if (valueAccessor && isExistingRecordAccessor) {
        const valueFieldPathArray = valueAccessor.split('.')
        const isExistingPathArray = isExistingRecordAccessor.split('.')

        if (valueFieldPathArray && isExistingPathArray) {
            const fieldValue = valueFieldPathArray.reduce(
                (o, p) => (o ? o[p] : undefined),
                submissionState
            ) // some code in stackoverflow that helps us here and I think is pretty genius https://stackoverflow.com/a/43849204
            const isExistingRecord = isExistingPathArray.reduce(
                (o, p) => (o ? o[p] : undefined),
                submissionState
            )

            if (
                isExistingRecord &&
                fieldValue !== null &&
                fieldValue !== undefined
            ) {
                //if it's an existing facility and the value exists then we set the field to read only, see: https://coordinatesolutions.atlassian.net/browse/AW-575?focusedCommentId=52415
                return true
            }
        }
    }
}

const getFieldDisabled = (
    field,
    dataAccessor,
    watch,
    userRoles,
    submissionState,
    data = null
) => {
    const isViewOnly = field.ViewOnly
    let disabledByRole = false
    // if (userRoles && field.DependsOnRoles && field.RoleLimitsActions === 'Edit') {
    //     const hasPermissionToEdit = field.DependsOnRoles.split(',').some(x => checkRole(x, userRoles))
    //     disabledByRole = !hasPermissionToEdit
    // }
    // // if (submissionState && field && field.DependsOnExistingFormField) {
    // //     return determineIfDependsOnExistingFormFieldTrue(field.DependsOnExistingFormField, submissionState)
    // // }
    // // CWP2-212 - disable fields on facility when isMrdb is true
    // if(dataAccessor === 'facility') {
    //     if (submissionState && submissionState.facility && submissionState.facility.isMrdb) {
    //         return true
    //     }
    // }
    // //CWP2-295 all existing records should be read-only, regardless of the user’s role
    // if (dataAccessor === 'sampleCogccrelated' && data && data.sampleCogccrelatedId) {
    //     return true
    // }

    // // CWP2-132 - disable fields on all tables based on their ids,
    // // if the user's role is not the form reviewer role
    // if (userRoles && !userRoles.includes('Administrator')) {
    //     if (dataAccessor === 'facility' && data && data.facilityId) {
    //         return true
    //     }
    //     if (dataAccessor === 'aquifer' && data && data.aquiferId) {
    //         return true
    //     }
    //     if (dataAccessor === 'sample' && data && data.sampleId) {
    //         return true
    //     }
    //     if (dataAccessor === 'resultBatch' && data && data.resultBatchId) {
    //         return true
    //     }
    //     if (dataAccessor === 'result' && data && data.resultId) {
    //         return true
    //     }
    //     if (dataAccessor === 'labBatchQc' && data && data.labBatchQcid) {
    //         return true
    //     }
    // }

    // // disable fields on address
    // if (dataAccessor === 'address') {
    //     const values = watch({nest: true})
    //     if (values && values[dataAccessor]) {
    //         const addressValues = values[dataAccessor]
    //         const copyToPhysicalAddressField = Object.keys(addressValues).find(x => x.includes('Copy'))
    //         if (addressValues[copyToPhysicalAddressField]) {
    //             if (field.UploadSectionGroupName === 'Mailing Address' && field.ColumnName !== 'MailingRecipient' && field.ColumnName !== 'CopyPhysicalAddress') {
    //                 return true
    //             }
    //         }
    //     }
    //     return ( isViewOnly ||  disabledByRole )
    // } else {
    return isViewOnly || disabledByRole
    // }
}

const FieldComponent = ({
    data,
    field,
    formInputSelector,
    getInputType,
    formWidth,
    formName,
    validate,
    idx,
    rowIdx,
    displayLabel,
    printable = false,
    submissionState,
    carefulRowData,
    rowData = null,
}) => {
    const { roles } = useContext(UserContext)
    const {
        register,
        unregister,
        control,
        setValue,
        clearErrors: clearError,
        formState: { errors },
        watch,
    } = useFormContext()
    const formComponent = formInputSelector(field.ControlType)
    const type = getInputType(field.DataType)
    const dataAccessor = field.DataAccessor

    const props = {
        comparisonOperators: comparisonOperatorParser(field.CompareItems),
        type,
        dataAccessor,
        registerParams: registerParamConstructor(field, true, carefulRowData),
        extraClass: getExtraClass(field.DataType),
        name: decapitalizeText(field.ColumnName), // de-capitalize the field accessors
        helper: field.Description,
        fieldName: field.Prompt,
        values: field.Values,
        minValue: field.MinValue,
        maxValue: field.MaxValue,
        maxLength: field.MaxLength,
        required: field.Required,
        defaultValue: field.DefaultValue,
        columnWidth: field.ColumnWidth || 175,
        dependsOnField: decapitalizeText(field.DependsOnField),
        dependsOnValue: field.DependsOnValue,
        disabled: getFieldDisabled(
            field,
            dataAccessor,
            watch,
            roles,
            submissionState,
            carefulRowData
        ),
    }
    // const error = errors ? errors[`${name}Value`] : null
    const fieldAccessorMap = {
        label: 'codedescription',
        value: 'code',
        type: type,
        active: 'active',
    }
    if (!formComponent) {
        return null
    }
    return React.createElement(formComponent, {
        register,
        unregister,
        key: `field-${idx}`,
        control,
        setValue,
        clearError,
        idx,
        formName,
        fieldAccessorMap,
        errors,
        formWidth,
        watch,
        displayLabel,
        rowIdx,
        printable,
        // disabled,
        rowData,
        ...props,
    })
}

export {
    getInputType,
    uploadInputSelector,
    uploadFieldParser,
    uploadValidate,
    formValidate,
    formInputSelector,
    FieldComponent,
}
export default formFieldParser
